<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  id: { type: String, required: true },
  modelValue: { type: String, required: true },
  label: { type: String, required: true },
  options: { type: Array, required: true },
  disabled: { type: Boolean, default: false },
  error: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);
const selectedLabel = computed(
  () =>
    props.options.find(option => option.value === props.modelValue)?.label || ''
);
</script>

<template>
  <div class="relative">
    <select
      :id="id"
      :value="modelValue"
      :disabled="disabled"
      :aria-label="label"
      :aria-invalid="!!error"
      :aria-describedby="error ? `${id}-error` : undefined"
      :title="`${label} ${selectedLabel}`"
      class="block w-full h-10 !mb-0 appearance-none bg-none rounded-lg border bg-n-alpha-1 ps-3 pe-10 text-sm text-transparent focus:border-n-brand disabled:cursor-not-allowed disabled:opacity-60"
      :class="error ? 'border-n-ruby-8' : 'border-n-weak'"
      @change="emit('update:modelValue', $event.target.value)"
    >
      <option value="" disabled hidden>{{ label }}</option>
      <option
        v-for="option in options"
        :key="option.value"
        :value="option.value"
        :disabled="option.disabled"
        class="text-n-slate-12"
      >
        {{ option.label }}
      </option>
    </select>
    <span
      aria-hidden="true"
      class="pointer-events-none absolute inset-y-0 start-3 end-10 flex items-center gap-1"
      :class="{ 'opacity-60': disabled }"
    >
      <span class="shrink-0 text-xs text-n-slate-11">{{ label }}</span>
      <span class="min-w-0 truncate text-sm text-n-slate-12">
        {{ selectedLabel }}
      </span>
    </span>
    <span
      aria-hidden="true"
      class="pointer-events-none absolute inset-y-px end-px flex w-9 items-center justify-center border-s border-n-weak text-n-slate-11"
      :class="{ 'opacity-60': disabled }"
    >
      <Icon icon="i-lucide-chevron-down" class="size-4" />
    </span>
  </div>
  <p v-if="error" :id="`${id}-error`" class="m-0 text-xs text-n-ruby-11">
    {{ error }}
  </p>
</template>
